import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  useClassifierCatalog,
  useEnsureClassifierCorpus,
} from '@/hooks/use-classifiers-admin'

export function ClassifierCorporaPage() {
  const navigate = useNavigate()
  const { data: catalog, isLoading, error } = useClassifierCatalog()
  const ensureCorpus = useEnsureClassifierCorpus()

  const openOrCreate = async (profileId: string, corpusId: string | null) => {
    try {
      if (corpusId) {
        navigate(`/platform/classifiers/${corpusId}`)
        return
      }
      const created = await ensureCorpus.mutateAsync(profileId)
      toast.success(`Seeded ${created.label}`)
      navigate(`/platform/classifiers/${created.id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open classifier')
    }
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        leading={<PageHeaderLeading />}
        title="Classifiers"
        description="Configure live classifier prompts and output contracts for this tenant. Publish a revision to change runtime behaviour."
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading catalog…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {(error as Error).message || 'Could not load classifier catalog.'}
        </p>
      ) : !catalog?.length ? (
        <p className="text-sm text-muted-foreground">
          No allowlisted live classifiers for this tenant. Enable{' '}
          <code className="text-xs">features.liveClassifiers</code> in client-config.
        </p>
      ) : (
        <div className="grid gap-4 @2col:grid-cols-2">
          {catalog.map((entry) => (
            <Card key={entry.profile_id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{entry.label}</CardTitle>
                  <Badge variant="secondary">{entry.capability_class}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground font-mono text-xs">{entry.profile_id}</p>
                <p className="text-muted-foreground">
                  Trigger: {entry.trigger}
                  {entry.current_version != null
                    ? ` · v${entry.current_version}`
                    : ' · not seeded'}
                  {entry.corpus_status ? ` · ${entry.corpus_status}` : ''}
                </p>
                <Button
                  type="button"
                  size="sm"
                  disabled={ensureCorpus.isPending}
                  onClick={() => void openOrCreate(entry.profile_id, entry.corpus_id)}
                >
                  {entry.corpus_id ? 'Open' : 'Seed & open'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  )
}
